import { ChangeDetectionStrategy, Component, computed, effect, inject, linkedSignal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { cloneDeep } from 'lodash-es';
import { getArrayOrEmptyArray } from 'src/app/core/array-utils';
import { Maybe, getIsNotNullable } from 'src/app/core/nullable-utils';
import { filterRecursively, mapRecursively } from 'src/app/core/object-utils';
import { createFinalUrl } from 'src/app/core/services/api/ix-rest-api';
import {
  FeatureFlagConfig,
  FeatureToggleService,
} from 'src/app/core/services/feature/feature-toggle.service';
import { LocalStorageSignalService } from 'src/app/core/services/local-storage-signal.service';
import { ViewAccessDto } from 'src/app/core/services/view-access-api.service';
import { ProjectStore } from 'src/app/core/state/project.store';
import { ViewAccessStore } from 'src/app/core/state/viewAccess.store';
import { WithRoles } from 'src/app/deliverable-profiles/mixins/with-roles.mixin';
import { MenuItemsFilterPipe } from 'src/app/shell/side-navigation/menu-items.pipe';
import { SideNavItem } from 'src/app/shell/side-navigation/side-nav/side-nav-item.model';
import { SideNavComponent } from 'src/app/shell/side-navigation/side-nav/side-nav.component';
import { subMenuStaggerAnimation, subMenuWidth } from './animations';
import { frozenMenuItems } from './menu-items';
import { getCurrentPath } from './side-navigation-util';

export const LS_KEY_SIDE_MENU_TOGGLE = 'SIDE_MENU_TOGGLE';

@Component({
  selector: 'app-side-navigation',
  templateUrl: './side-navigation.component.html',
  styleUrl: './side-navigation.component.scss',
  animations: [subMenuStaggerAnimation, subMenuWidth],
  imports: [SideNavComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SideNavigationComponent extends WithRoles() {
  readonly #router = inject(Router);
  readonly #projectStore = inject(ProjectStore);
  readonly #viewAccessStore = inject(ViewAccessStore);
  readonly #featureToggleService = inject(FeatureToggleService);
  readonly #lsService = inject(LocalStorageSignalService);

  readonly isLoading = this.#viewAccessStore.isLoading;
  readonly isMenuExpanded = this.#lsService.signal<boolean>(LS_KEY_SIDE_MENU_TOGGLE, false);
  readonly #currentPath = toSignal(getCurrentPath(this.#router));

  readonly #menuItems = computed(() => {
    const viewAccess = this.#viewAccessStore.viewAccess();
    const allFeatureToggles = this.#featureToggleService.allToggles();
    const projectId = this.#projectStore.currentProjectId();

    const items = toSideNav(viewAccess);
    const batchDownloadNavItem = items.findIndex(x => x.id === 'batch-download');
    const canAccessBatchDownload = this.isSuperUser() || this.isAdmin() || this.isSupplierManager();
    if (batchDownloadNavItem && !canAccessBatchDownload) {
      items.splice(batchDownloadNavItem, 1);
    }

    const itemsWithRouterLinks = mapRecursively(items, toItemWithRouterLink(projectId));
    return filterRecursively(itemsWithRouterLinks, onlyEnabledRoutes(allFeatureToggles));
  });

  readonly sideNavItems = linkedSignal({
    source: () => ({
      items: this.#menuItems(),
      path: this.#currentPath(), // Update active state based on path changes
    }),
    computation: source => mapRecursively(source.items, toItemWithActiveAndExpandedFlags(this.#router)),
  });

  constructor() {
    super();
    effect(() => {
      const isMenuExpanded = this.isMenuExpanded();
      this.#lsService.set(LS_KEY_SIDE_MENU_TOGGLE, isMenuExpanded);
    });
  }

  toggleSideNav(): void {
    this.#lsService.set(LS_KEY_SIDE_MENU_TOGGLE, !this.isMenuExpanded());
  }
}

function toSideNav(viewAccesses: ViewAccessDto[]): SideNavItem[] {
  const allMenuItems = cloneDeep(frozenMenuItems);
  return new MenuItemsFilterPipe().transform(allMenuItems, viewAccesses);
}

function toItemWithRouterLink(projectId: number | undefined): (item: SideNavItem) => SideNavItem {
  return item => {
    let routerLink: string | undefined = createFinalUrl({
      pathname: getArrayOrEmptyArray(item.url).join('/'),
      options: { path: { projectId } },
    });
    routerLink = routerLink === '' ? undefined : routerLink;

    return { ...item, routerLink };
  };
}

function toItemWithActiveAndExpandedFlags(router: Router): (item: SideNavItem) => SideNavItem {
  return item => {
    let isActive = false;
    let isExpanded = false;

    if (typeof item.routerLink === 'string') {
      isActive = getIsPathMatcingActiveRoute(router, item.routerLink.split('/'));
    } else if (getIsNotNullable(item.children)) {
      isExpanded = item.children.some(child => child.isActive || child.isExpanded);
    }

    return { ...item, isActive, isExpanded };
  };
}

function getIsPathMatcingActiveRoute(router: Router, pathFragments: string[]): boolean {
  const urlTree = router.createUrlTree(pathFragments);
  return router.isActive(urlTree, {
    paths: 'subset',
    queryParams: 'subset',
    fragment: 'ignored',
    matrixParams: 'ignored',
  });
}

function onlyEnabledRoutes(toggles: Maybe<FeatureFlagConfig>): (item: SideNavItem) => boolean {
  return item =>
    getIsNotNullable(toggles) && getIsNotNullable(item.featureFlagKey)
      ? toggles[item.featureFlagKey]?.value
      : true;
}
